/**
 * AdminBhiRiskFactorsPage — iter980 · Sprint 1
 *
 * Drag-and-drop YAML upload UI for the `bhi_risk_factors` collection.
 *
 * Flow:
 *   drop files → auto-preview (POST /upload-preview) → user reviews
 *   diff/errors/warnings → clicks "Ingest" (POST /upload-ingest) → done.
 *
 * Uses only admin-scoped endpoints; the route itself is guarded by
 * ProtectedRoute + backend `_require_admin`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileCode2, ChevronLeft, Loader2, Trash2, PlayCircle,
  BookOpen, CheckCircle2, AlertCircle, XCircle, Info, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  new:       { icon: CheckCircle2, cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30", label: "Нов" },
  updated:   { icon: RefreshCw,    cls: "text-amber-500 bg-amber-500/10 border-amber-500/30",       label: "Обновен" },
  unchanged: { icon: Info,         cls: "text-slate-500 bg-slate-500/10 border-slate-500/30",       label: "Без промяна" },
  error:     { icon: XCircle,      cls: "text-rose-500 bg-rose-500/10 border-rose-500/30",          label: "Грешка" },
};

export default function AdminBhiRiskFactorsPage() {
  const { API, user } = useAuth();
  const { isDark } = useTheme();

  const [files, setFiles] = useState([]);   // File[]
  const [dragActive, setDragActive] = useState(false);
  const [uploadMode, setUploadMode] = useState("merge"); // "merge" | "replace"
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // {items, counts, total}
  const [ingesting, setIngesting] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState([]);
  const [detailDoc, setDetailDoc] = useState(null);
  const inputRef = useRef(null);

  const loadExisting = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bhi-risk-factors/list`);
      setExisting(data.items || []);
    } catch {
      /* silent — non-blocking */
    }
  }, [API]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  // ---------------- Drop / file input ----------------
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    const filtered = dropped.filter((f) =>
      /\.(ya?ml|txt)$/i.test(f.name)
    );
    if (dropped.length && !filtered.length) {
      toast.error("Само .yaml / .yml / .txt файлове са позволени.");
      return;
    }
    setFiles((prev) => [...prev, ...filtered]);
    setPreview(null); setResult(null);
  }, []);

  const onFileInput = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...chosen]);
    setPreview(null); setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((s) => s.filter((_, i) => i !== idx));
  const clearAll = () => { setFiles([]); setPreview(null); setResult(null); };

  // ---------------- Preview ----------------
  const runPreview = async () => {
    if (!files.length) return;
    setPreviewing(true); setResult(null);
    const endpoint = uploadMode === "merge"
      ? "upload-merge-preview"
      : "upload-preview";
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f, f.name));
      const { data } = await axios.post(
        `${API}/admin/bhi-risk-factors/${endpoint}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      const nErr = data.counts?.error || 0;
      if (nErr) toast.warning(`Preview: ${nErr} файл(а) с грешки`);
      else      toast.success(`Preview (${uploadMode}): ${data.total} файл(а) готови за ingest`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Грешка при preview.";
      toast.error(typeof detail === "string" ? detail : "Грешка при preview.");
    } finally {
      setPreviewing(false);
    }
  };

  // ---------------- Ingest ----------------
  const runIngest = async () => {
    if (!files.length) return;
    setIngesting(true);
    const endpoint = uploadMode === "merge"
      ? "upload-merge-commit"
      : "upload-ingest";
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f, f.name));
      const { data } = await axios.post(
        `${API}/admin/bhi-risk-factors/${endpoint}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      const label = uploadMode === "merge" ? "Merged" : "Ingested";
      toast.success(
        `${label}: ${data.inserted} нови, ${data.updated} обновени, ${data.skipped_error} прескочени`
      );
      await loadExisting();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Грешка при ingest.";
      toast.error(typeof detail === "string" ? detail : "Грешка при ingest.");
    } finally {
      setIngesting(false);
    }
  };

  // ---------------- Delete existing factor ----------------
  const deleteFactor = async (fid) => {
    if (!window.confirm(`Изтриване на риск-фактор "${fid}"?`)) return;
    try {
      await axios.delete(`${API}/admin/bhi-risk-factors/${fid}`);
      toast.success(`Изтрит: ${fid}`);
      await loadExisting();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Грешка при изтриване.";
      toast.error(typeof detail === "string" ? detail : "Грешка при изтриване.");
    }
  };

  const viewDetail = async (fid) => {
    try {
      const { data } = await axios.get(`${API}/admin/bhi-risk-factors/${fid}`);
      setDetailDoc(data);
    } catch {
      toast.error("Грешка при зареждане на детайлите.");
    }
  };

  // iter1013 — Pathway activations editor
  const [pwEditor, setPwEditor] = useState(null);  // { factor_id, doc, catalog }
  const [pwText, setPwText] = useState("");
  const openPathwayEditor = async (fid) => {
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/admin/bhi-risk-factors/${fid}`),
        axios.get(`${API}/admin/bhi-risk-factors/pathway-meta/catalog`),
      ]);
      setPwEditor({ factor_id: fid, doc: d.data, catalog: c.data.items });
      setPwText(JSON.stringify(d.data?.pathway_activations || {}, null, 2));
    } catch (e) {
      toast.error("Грешка при отваряне на редактора.");
    }
  };
  const savePathwayEditor = async () => {
    try {
      const parsed = JSON.parse(pwText || "{}");
      await axios.put(
        `${API}/admin/bhi-risk-factors/${pwEditor.factor_id}/pathway-activations`,
        { pathway_activations: parsed },
      );
      toast.success(`✓ ${pwEditor.factor_id} — pathways обновени`);
      setPwEditor(null);
      loadExisting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис.");
    }
  };

  // ---------------- UI ----------------
  const canIngest = useMemo(
    () => !!preview && preview.total > 0 && (preview.counts?.error || 0) < preview.total,
    [preview],
  );

  const themeBg = isDark ? "bg-slate-900" : "bg-slate-50";
  const cardCls = isDark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200";
  const mutedCls = isDark ? "text-slate-400" : "text-slate-500";

  if (user?.role !== "admin") {
    return (
      <div className={`min-h-screen ${themeBg} flex items-center justify-center p-4`} data-testid="admin-bhi-rf-forbidden">
        <div className={`${cardCls} border rounded-xl p-6 max-w-md text-center`}>
          <AlertCircle className="w-8 h-8 mx-auto text-rose-500 mb-3" />
          <div className="text-lg font-semibold mb-1">Само за администратори</div>
          <div className={`text-sm ${mutedCls}`}>Тази страница изисква admin роля.</div>
          <Link to="/dashboard" className="inline-block mt-4 text-sm text-emerald-500 hover:underline">
            ← Обратно към Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeBg} pb-16`} data-testid="admin-bhi-rf-page">
      {/* Header */}
      <div className={`${cardCls} border-b sticky top-0 z-10 backdrop-blur bg-opacity-90`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/admin" className="text-sm hover:underline flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Admin
          </Link>
          <div className="flex-1">
            <h1 className="text-lg md:text-xl font-bold">BHI Рискови фактори — Качване</h1>
            <p className={`text-xs ${mutedCls}`}>
              Пусни .yaml/.txt файлове. Preview парсва + валидира + прави diff; Ingest записва в базата.
            </p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${cardCls} border`}>
            В база: <b>{existing.length}</b>
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* iter1028 — YAML bulk export toolbar */}
        <div className={`${cardCls} border rounded-xl p-3 flex flex-wrap items-center gap-2`} data-testid="admin-bhi-rf-yaml-toolbar">
          <div className="text-xs flex-1 min-w-[200px]">
            <div className="font-semibold">📦 YAML файлове за клинична ревизия</div>
            <div className={mutedCls}>Свалете 42-те файла, ревизирайте, качете обратно през drop-zone долу.</div>
          </div>
          <a
            href={`${API}/admin/bhi-risk-factors/yaml-bundle/all`}
            target="_blank" rel="noopener noreferrer"
            data-testid="admin-bhi-rf-yaml-bundle"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold"
          >
            <FileCode2 className="w-3.5 h-3.5" /> Свали всичките (ZIP)
          </a>
          <button
            type="button"
            data-testid="admin-bhi-rf-yaml-refresh"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/admin/bhi-risk-factors/yaml-export-refresh`);
                toast.success(`Regenerated ${r.data.files_written} YAML файла от DB`);
              } catch (e) {
                toast.error("Regen failed: " + (e?.response?.data?.detail || e.message));
              }
            }}
          >
            <PlayCircle className="w-3.5 h-3.5" /> Regen от DB
          </button>
        </div>

        {/* Drop zone */}
        <div
          className={`${cardCls} border-2 border-dashed rounded-xl p-8 transition ${
            dragActive ? "border-emerald-500 bg-emerald-500/5" : ""
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          data-testid="admin-bhi-rf-dropzone"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center">
              <Upload className="w-7 h-7" />
            </div>
            <div>
              <div className="font-semibold">Пусни YAML файлове тук</div>
              <div className={`text-xs ${mutedCls} mt-1`}>
                Или <button type="button" className="text-emerald-500 hover:underline"
                    onClick={() => inputRef.current?.click()}>избери от компютъра</button>
                {" "}(.yaml, .yml, .txt · max 512KB × 60 files)
              </div>
            </div>

            {/* iter1029 — Upload mode selector */}
            <div
              className={`mt-2 inline-flex rounded-lg border overflow-hidden ${isDark ? "border-slate-700" : "border-slate-300"}`}
              data-testid="admin-bhi-rf-upload-mode"
            >
              <button
                type="button"
                onClick={() => { setUploadMode("merge"); setPreview(null); setResult(null); }}
                data-testid="admin-bhi-rf-mode-merge"
                className={`px-3 py-1.5 text-xs font-semibold transition ${
                  uploadMode === "merge"
                    ? "bg-emerald-600 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
              >
                🧬 Merge (запазва UI полета)
              </button>
              <button
                type="button"
                onClick={() => { setUploadMode("replace"); setPreview(null); setResult(null); }}
                data-testid="admin-bhi-rf-mode-replace"
                className={`px-3 py-1.5 text-xs font-semibold transition border-l ${isDark ? "border-slate-700" : "border-slate-300"} ${
                  uploadMode === "replace"
                    ? "bg-rose-600 text-white"
                    : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-50")
                }`}
              >
                ⚠️ Replace (пълна замяна)
              </button>
            </div>
            <div className={`text-[11px] ${mutedCls} max-w-md`}>
              {uploadMode === "merge"
                ? "Merge пази levels, activated_pathways, ui, pathway_activations от текущия DB и наслоява новите клинични секции. Ако YAML съдържа platform_weights — теглата се синхронизират."
                : "Replace презаписва целия документ. Оперативните полета в DB ще бъдат изтрити, ако не са в YAML файла."}
            </div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".yaml,.yml,.txt"
              hidden
              onChange={onFileInput}
              data-testid="admin-bhi-rf-file-input"
            />
          </div>
        </div>

        {/* Queued files */}
        {files.length > 0 && (
          <div className={`${cardCls} border rounded-xl p-4 space-y-3`} data-testid="admin-bhi-rf-queue">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm">Опашка: {files.length} файл(а)</div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={clearAll} data-testid="admin-bhi-rf-clear">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Изчисти
                </Button>
                <Button
                  size="sm"
                  onClick={runPreview}
                  disabled={previewing}
                  data-testid="admin-bhi-rf-preview"
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                >
                  {previewing
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <FileCode2 className="w-3.5 h-3.5 mr-1.5" />}
                  Preview
                </Button>
                <Button
                  size="sm"
                  onClick={runIngest}
                  disabled={!canIngest || ingesting}
                  data-testid="admin-bhi-rf-ingest"
                  className={`${uploadMode === "merge" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"} text-white disabled:bg-slate-300 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-300`}
                >
                  {ingesting
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
                  {uploadMode === "merge" ? "Merge commit" : "Ingest (replace)"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className={`text-xs flex items-center gap-2 border rounded px-2 py-1.5 ${
                  isDark ? "border-slate-700" : "border-slate-200"
                }`}>
                  <FileCode2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className={mutedCls}>{Math.round(f.size / 1024)}KB</span>
                  <button className="hover:text-rose-500" onClick={() => removeFile(i)} title="Премахни">
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview diff */}
        {preview && (
          <div className={`${cardCls} border rounded-xl p-4 space-y-3`} data-testid="admin-bhi-rf-preview-result">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-semibold text-sm">Preview:</div>
              {["new", "updated", "unchanged", "error"].map((k) => {
                const s = STATUS_STYLE[k];
                const Icon = s.icon;
                const n = preview.counts?.[k] || 0;
                return (
                  <span key={k} className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${s.cls}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {s.label}: <b>{n}</b>
                  </span>
                );
              })}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={mutedCls}>
                    <th className="text-left py-1.5 pr-3">Файл</th>
                    <th className="text-left pr-3">Статус</th>
                    <th className="text-left pr-3">ID</th>
                    <th className="text-left pr-3">Име</th>
                    <th className="text-left pr-3">Категория</th>
                    <th className="text-left pr-3">Mod</th>
                    <th className="text-left pr-3">Нива</th>
                    <th className="text-left pr-3">Pathways</th>
                    <th className="text-left">Диагностика</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it, i) => {
                    const s = STATUS_STYLE[it.status] || STATUS_STYLE.error;
                    const Icon = s.icon;
                    return (
                      <tr key={i} className={isDark ? "border-t border-slate-700" : "border-t border-slate-200"}
                          data-testid={`preview-row-${it.id || i}`}>
                        <td className="py-1.5 pr-3 truncate max-w-[180px]">{it.source}</td>
                        <td className="pr-3">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${s.cls} border`}>
                            <Icon className="w-3 h-3" /> {s.label}
                          </span>
                        </td>
                        <td className="pr-3 font-mono">{it.id || "—"}</td>
                        <td className="pr-3">{it.preview?.name_bg || "—"}</td>
                        <td className="pr-3">{it.preview?.category || "—"}</td>
                        <td className="pr-3">{it.preview?.modifiable ? "✓" : "—"}</td>
                        <td className="pr-3">{it.preview?.levels_count ?? "—"}</td>
                        <td className="pr-3">{it.preview?.pathway_count ?? "—"}</td>
                        <td>
                          {it.errors?.length > 0 && (
                            <div className="text-rose-500">
                              {it.errors.map((e, j) => <div key={j}>• {e}</div>)}
                            </div>
                          )}
                          {it.warnings?.length > 0 && (
                            <div className="text-amber-500">
                              {it.warnings.map((w, j) => <div key={j}>⚠ {w}</div>)}
                            </div>
                          )}
                          {it.changed_fields?.length > 0 && (
                            <div className={mutedCls}>
                              Δ {it.changed_fields.join(", ")}
                            </div>
                          )}
                          {it.preserved_fields?.length > 0 && (
                            <div className="text-emerald-500/80" title="Полета, запазени от текущия DB doc (не се пипат)">
                              🔒 {it.preserved_fields.join(", ")}
                            </div>
                          )}
                          {it.preview?.weight_changes && Object.keys(it.preview.weight_changes).length > 0 && (
                            <div className="text-purple-400">
                              ⚖ {Object.entries(it.preview.weight_changes).map(([k,v]) => `${k}: ${v.old}→${v.new}`).join(", ")}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ingest result */}
        {result && (
          <div className={`${cardCls} border-2 border-emerald-500/40 rounded-xl p-4`} data-testid="admin-bhi-rf-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <div className="font-semibold">Ingest завърши</div>
            </div>
            <div className="text-sm space-y-1">
              <div>✓ Вкарани: <b>{result.inserted}</b></div>
              <div>↻ Обновени: <b>{result.updated}</b></div>
              <div>⚠ Прескочени (грешки): <b>{result.skipped_error}</b></div>
              <div>Общо в базата сега: <b>{result.total_after}</b></div>
            </div>
          </div>
        )}

        {/* Existing DB inventory */}
        <div className={`${cardCls} border rounded-xl p-4`} data-testid="admin-bhi-rf-existing">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sm">Активни в базата ({existing.length})</div>
            <Button size="sm" variant="ghost" onClick={loadExisting}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Обнови
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-inherit">
                <tr className={mutedCls}>
                  <th className="text-left py-1.5 pr-3">ID</th>
                  <th className="text-left pr-3">Име</th>
                  <th className="text-left pr-3">Категория</th>
                  <th className="text-left pr-3">Mod</th>
                  <th className="text-left pr-3">Pathways</th>
                  <th className="text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {existing.map((it) => (
                  <tr key={it.id} className={isDark ? "border-t border-slate-700" : "border-t border-slate-200"}>
                    <td className="py-1.5 pr-3 font-mono">{it.id}</td>
                    <td className="pr-3">{it.name_bg}</td>
                    <td className="pr-3">{it.category}</td>
                    <td className="pr-3">{it.modifiable ? "✓" : "—"}</td>
                    <td className="pr-3">{it.pathway_count}</td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        className="text-sky-500 hover:underline mr-3"
                        onClick={() => viewDetail(it.id)}
                        data-testid={`admin-bhi-rf-view-${it.id}`}
                      >
                        <BookOpen className="w-3.5 h-3.5 inline" /> Детайли
                      </button>
                      <button
                        className="text-emerald-500 hover:underline mr-3"
                        onClick={() => openPathwayEditor(it.id)}
                        data-testid={`admin-bhi-rf-paths-${it.id}`}
                        title="Редакция на pathway_activations"
                      >
                        ✎ Pathways
                      </button>
                      <a
                        className="text-purple-500 hover:underline mr-3"
                        href={`${API}/admin/bhi-risk-factors/yaml/${it.id}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`admin-bhi-rf-yaml-${it.id}`}
                        title="Свали YAML файл за ревизия"
                      >
                        ⬇ YAML
                      </a>
                      <button
                        className="text-rose-500 hover:underline"
                        onClick={() => deleteFactor(it.id)}
                        data-testid={`admin-bhi-rf-delete-${it.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 inline" /> Изтрий
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detailDoc} onOpenChange={(o) => !o && setDetailDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-emerald-500" />
              {detailDoc?.name_bg} <span className="text-xs font-mono opacity-60">({detailDoc?.id})</span>
            </DialogTitle>
            <DialogDescription>
              Пълният JSON запис от MongoDB — за одит и debugging.
            </DialogDescription>
          </DialogHeader>
          {detailDoc && (
            <pre className={`text-[11px] leading-snug whitespace-pre-wrap break-words font-mono p-3 rounded ${
              isDark ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-800"
            }`}>
              {JSON.stringify(detailDoc, null, 2)}
            </pre>
          )}
        </DialogContent>
      </Dialog>

      {/* iter1013 — Pathway activations editor */}
      <Dialog open={!!pwEditor} onOpenChange={(o) => { if (!o) setPwEditor(null); }}>
        <DialogContent className="max-w-2xl bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 border border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Pathway activations · {pwEditor?.factor_id}</DialogTitle>
            <DialogDescription className="text-xs">
              JSON map {"{level_key: [semantic_pathway_key, …]}"}. Всеки path
              трябва да съществува в services/condition_pathway_map.PATHWAY_META.
            </DialogDescription>
          </DialogHeader>
          {pwEditor ? (
            <div className="space-y-3 text-xs">
              {/* iter1025 — Show ACTUAL activated_pathways (17 items) read-only
                  so admin doesn't confuse them with pathway_activations map. */}
              <div>
                <div className="font-semibold mb-1 flex items-center gap-2">
                  Активирани pathways
                  <span className="text-[10px] font-normal opacity-70">
                    (<code>activated_pathways</code> · {(pwEditor.doc?.activated_pathways || []).length} записа)
                  </span>
                </div>
                <div
                  data-testid="admin-pw-editor-active-list"
                  className={`max-h-40 overflow-auto p-2 rounded font-mono text-[10px] space-y-0.5 ${
                    isDark ? "bg-slate-900" : "bg-slate-50"
                  }`}
                >
                  {(pwEditor.doc?.activated_pathways || []).length === 0 ? (
                    <div className="opacity-60 italic">Няма записани pathways за този фактор.</div>
                  ) : (
                    (pwEditor.doc?.activated_pathways || []).map((p, i) => {
                      const pid = (p && typeof p === "object")
                        ? (p.pathway_id || p.id || p.name || p.key)
                        : String(p);
                      const w = (p && typeof p === "object") ? (p.weight ?? p.w) : null;
                      const bw = (p && typeof p === "object") ? p.base_weight : null;
                      return (
                        <div key={`${pid}-${i}`} className="flex items-center gap-2">
                          <span className="opacity-40 w-4 text-right">{i + 1}.</span>
                          <span className="flex-1">{pid}</span>
                          {w != null && (
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              w >= 8 ? "bg-red-500/20 text-red-600 dark:text-red-300"
                              : w >= 6 ? "bg-amber-500/20 text-amber-600 dark:text-amber-300"
                              : w >= 4 ? "bg-lime-500/20 text-lime-600 dark:text-lime-300"
                              : "bg-slate-500/20 text-slate-600 dark:text-slate-300"
                            }`}>
                              w={w}
                            </span>
                          )}
                          {bw != null && bw !== w && (
                            <span className="text-[9px] opacity-50">base={bw}</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <p className="text-[10px] opacity-60 mt-1 leading-snug">
                  Тези pathways идват от YAML upload или migration
                  (<code>iter1024_seed_bhi_weights</code>). За bulk редакция преизкачете YAML файла.
                </p>
              </div>

              <div>
                <div className="font-semibold mb-1">Available levels:</div>
                <div className="flex flex-wrap gap-1">
                  {(pwEditor.doc?.levels || []).map((l) => (
                    <code key={l.key}
                      className={`px-2 py-0.5 rounded ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
                      {l.key}
                    </code>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1">Pathway catalog ({pwEditor.catalog.length} keys):</div>
                <div className={`max-h-32 overflow-auto p-2 rounded font-mono text-[10px] ${isDark ? "bg-slate-900" : "bg-slate-50"}`}>
                  {pwEditor.catalog.map((c) => (
                    <div key={c.key}>{c.key} · {c.name_bg}</div>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-semibold mb-1 flex items-center gap-2">
                  Level-базирано активиране
                  <span className="text-[10px] font-normal opacity-70">
                    (<code>pathway_activations</code> · за specific level overrides)
                  </span>
                </div>
                <textarea
                  data-testid="admin-pw-editor-textarea"
                  value={pwText}
                  onChange={(e) => setPwText(e.target.value)}
                  className={`w-full h-56 font-mono text-[11px] p-2 rounded border ${
                    isDark ? "bg-slate-900 border-slate-700 text-slate-100"
                           : "bg-white border-slate-300 text-slate-900"
                  }`}
                />
                <p className="text-[10px] opacity-60 mt-1 leading-snug">
                  Формат: <code>{"{ \"level_key\": [\"semantic_pathway_key\", …] }"}</code>.
                  Използва се когато конкретно ниво (напр. Diabetes T2 vs T1) активира различни pathways.
                  За обща активация редактирайте <code>activated_pathways</code> през YAML upload по-горе.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button className="px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                        onClick={() => setPwEditor(null)}>Отказ</button>
                <button
                  data-testid="admin-pw-editor-save"
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={savePathwayEditor}
                >
                  Запази
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
